import { createRoute, Link } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { Route as patientLayout } from "./patient-layout";
import { cn } from "@/frontend/lib/utils";
import { PageShell } from "@/frontend/components/dashboard/PageShell";
import { Button } from "@/frontend/components/ui/button";
import { PatientPageHeader } from "@/frontend/components/dashboard/patient/PatientPageHeader";
import { NextStepCard } from "@/frontend/components/dashboard/patient/NextStepCard";
import { PatientDetailsCard } from "@/frontend/components/dashboard/patient/PatientDetailsCard";
import { RecordSourcesCard } from "@/frontend/components/dashboard/patient/RecordSourcesCard";
import { RecentActivityCard } from "@/frontend/components/dashboard/patient/RecentActivityCard";
import { DeletePatientDialog } from "@/frontend/components/dashboard/patient/DeletePatientDialog";
import { PatientDangerZone } from "@/frontend/components/dashboard/patient/PatientDangerZone";
import { useWorkspacePersona } from "@/frontend/components/dashboard/individual/useWorkspacePersona";
import { useSession } from "@/frontend/auth-client";
import {
  prefetchPatientSummary,
  useActiveMemberQuery,
  usePatientSummaryQuery,
} from "@/frontend/queries/dashboard/patients";

export const Route = createRoute({
  getParentRoute: () => patientLayout,
  path: "/summary",
  // the cards' requests start with the summary's, not after it
  loader: ({ params }) => prefetchPatientSummary(params.patientId),
  component: PatientSummaryPage,
});

/**
 * Patient summary, with patient details leading at the full page width and the
 * two data panels side by side under it. The page waits for both the summary and
 * the workspace persona, since painting the workspace variant first would flash
 * magic-link copy at an individual. Every card renders only once it has
 * something to say.
 */
function PatientSummaryPage() {
  const { patientId } = Route.useParams();
  const { isIndividual, isLoading: isPersonaLoading } = useWorkspacePersona();
  const { data: summary, isLoading, isError } = usePatientSummaryQuery(patientId);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Edit is admin/owner-gated to mirror updatePatientDetails' server-side scope.
  const { data: session } = useSession();
  const isPlatformAdmin = session?.user?.role === "admin";
  const activeMemberQuery = useActiveMemberQuery(!!session && !isPlatformAdmin);
  const roles = (activeMemberQuery.data?.role ?? "")
    .split(",")
    .map((role) => role.trim())
    .filter(Boolean);
  const canEditDetails =
    isPlatformAdmin || roles.some((role) => ["admin", "owner"].includes(role));

  return (
    <div className="flex min-h-svh flex-col lg:h-svh lg:min-h-0 lg:overflow-hidden">
      <PatientPageHeader patientId={patientId} />

      <PageShell className="flex min-h-0 flex-1 flex-col py-6 lg:overflow-hidden">
        {isLoading || isPersonaLoading ? (
          <div className="flex flex-1 items-center justify-center py-16">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </div>
        ) : !summary ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 py-16 text-center">
            <p className="text-body font-medium">This patient is not in this workspace</p>
            <p className="text-caption text-muted-foreground">
              {isError
                ? "The patient could not be loaded."
                : "It may have been deleted, or the link points at another workspace."}
            </p>
            <Button asChild variant="outline" size="sm">
              <Link to="/dashboard/patients">Back to patients</Link>
            </Button>
          </div>
        ) : (
          <>
            <div className="flex min-h-0 flex-1 flex-col gap-5">
              <PatientDetailsCard summary={summary} canEdit={canEditDetails} />
              <NextStepCard patientId={patientId} summary={summary} />
              <div
                className={cn(
                  "grid min-h-0 flex-1 items-start gap-5",
                  summary.row.healthSystemCount > 0 && "lg:grid-cols-2",
                )}
              >
                <RecordSourcesCard patientId={patientId} />
                <RecentActivityCard patientId={patientId} />
              </div>
              <PatientDangerZone
                isIndividual={isIndividual}
                onDelete={() => setConfirmDelete(true)}
              />
            </div>

            <DeletePatientDialog
              patientMembershipId={patientId}
              patientName={summary.row.name}
              open={confirmDelete}
              onOpenChange={setConfirmDelete}
            />
          </>
        )}
      </PageShell>
    </div>
  );
}
